import { FC, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { useGetCompanyMutualFundMarks, useGetCompanyZanbatoMarksValuation, useGetCompanyZanbatoReportedMarks } from 'services/hooks/companies';
import { CardBody, CardTitle, CardTitleRow, OutlinedCard } from '../CompanyOverview.styles';
import { EdgarMarksBody } from './EdgarMarksBody';
import { TabButton, TabRow } from './MutualFundMarksCard.styles';
import {
  buildMutualFundMarksSourceRows,
  EndDateModeValue,
  Period,
  PeriodValue,
  ViewMode,
  ViewModeValue,
} from './MutualFundMarksCard.utils';
import MutualFundMarksPreviewModal from './MutualFundMarksPreviewModal';
import { ZanbatoMarksBody } from '../ZanbatoReportedMarksCard';
import {
  DEFAULT_ENABLED_IDS,
  ZanbatoMarksValuationResponse,
  ZanbatoReportedMarksResponse,
} from '../ZanbatoReportedMarksCard/ZanbatoReportedMarksCard.utils';

const MarkSource = { Edgar: 'edgar', Zanbato: 'zanbato' } as const;
type MarkSourceValue = typeof MarkSource[keyof typeof MarkSource];

interface Props {
  companyId: number | undefined;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  navigateToValuations?: () => void;
}

const MutualFundMarksCard: FC<Props> = ({ companyId, measurementDate, endDateMode, navigateToValuations }) => {
  const [source, setSource] = useState<MarkSourceValue>(MarkSource.Edgar);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [view, setView] = useState<ViewModeValue>(ViewMode.Chart);
  const [edgarPeriod, setEdgarPeriod] = useState<PeriodValue>(Period.Y1);
  const [zanbatoPeriod, setZanbatoPeriod] = useState<PeriodValue>(Period.Y1);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(DEFAULT_ENABLED_IDS);

  const { data: edgarData, isLoading: edgarLoading } = useGetCompanyMutualFundMarks(companyId);
  const { data: zanbatoData, isLoading: zanbatoLoading } = useGetCompanyZanbatoReportedMarks(companyId);
  const { data: valuationData, isLoading: valuationLoading } = useGetCompanyZanbatoMarksValuation(companyId);

  const edgarMarks = useMemo(() => edgarData?.marks ?? [], [edgarData?.marks]);
  const zanbatoMarks = useMemo(
    () => (valuationData as ZanbatoMarksValuationResponse | undefined)?.valuations ?? [],
    [valuationData]
  );
  const hasInWindowPreviewRows = useMemo(() => {
    if (!measurementDate) {
      return false;
    }
    return buildMutualFundMarksSourceRows(zanbatoMarks, edgarMarks, measurementDate).length > 0;
  }, [zanbatoMarks, edgarMarks, measurementDate]);

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <CardTitle>Mutual Fund Marks</CardTitle>
          {navigateToValuations && measurementDate && !edgarLoading && !valuationLoading && hasInWindowPreviewRows && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setPreviewModalOpen(true)}
              data-testid="mutual-fund-marks-card__create-mutual-fund-marks-approach"
              sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0, ml: 1 }}>
              Create Mutual Fund Marks Approach
            </Button>
          )}
        </CardTitleRow>

        <TabRow>
          <TabButton
            active={source === MarkSource.Edgar}
            onClick={() => setSource(MarkSource.Edgar)}
          >
            EDGAR
          </TabButton>
          <TabButton
            active={source === MarkSource.Zanbato}
            onClick={() => setSource(MarkSource.Zanbato)}
          >
            Zanbato
          </TabButton>
        </TabRow>

        {source === MarkSource.Edgar && (
          <EdgarMarksBody
            data={edgarData}
            isLoading={edgarLoading}
            measurementDate={measurementDate}
            endDateMode={endDateMode}
            view={view}
            onViewChange={setView}
            period={edgarPeriod}
            onPeriodChange={setEdgarPeriod}
          />
        )}
        {source === MarkSource.Zanbato && (
          <ZanbatoMarksBody
            data={zanbatoData as ZanbatoReportedMarksResponse | undefined}
            isLoading={zanbatoLoading}
            valuationData={valuationData as ZanbatoMarksValuationResponse | undefined}
            isValuationLoading={valuationLoading}
            measurementDate={measurementDate}
            endDateMode={endDateMode}
            view={view}
            onViewChange={setView}
            period={zanbatoPeriod}
            onPeriodChange={setZanbatoPeriod}
            enabledIds={enabledIds}
            onEnabledIdsChange={setEnabledIds}
          />
        )}
      </CardBody>

      {measurementDate && (
        <MutualFundMarksPreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          onAddToValuation={() => {
            setPreviewModalOpen(false);
            navigateToValuations?.();
          }}
          zanbatoMarks={zanbatoMarks}
          edgarMarks={edgarMarks}
          measurementDate={measurementDate}
        />
      )}
    </OutlinedCard>
  );
};

export { MutualFundMarksCard };
